/**
 * MH-02 Danh sách thiết bị — màn hình làm việc chính của quản trị viên thiết bị,
 * thiết kế cho quy mô đến 5.000 dòng trong phạm vi một tỉnh.
 *
 * Bốn điểm bắt buộc, tất cả đều hiện thực ở tệp này:
 *  1. đủ cột của bản mẫu "Cụm loa" (deviceView) và đủ NĂM trạng thái Bảng 3.3;
 *  2. lọc, đếm, phân trang ở phía máy chủ; mặc định 20 dòng, tối đa 200;
 *  3. bộ lọc ghi vào địa chỉ trang để chia sẻ được bằng đường dẫn, và nút xóa lọc
 *     đưa về MẶC ĐỊNH THEO PHẠM VI ĐỊA BÀN của người dùng, không về rỗng;
 *  4. tự làm mới 60 giây, DỪNG khi tab ẩn hoặc khi người dùng đang chọn dòng.
 *
 * Bố cục theo bản mẫu deviceView: hàng lọc trên + Xuất Excel; ba ô tóm tắt
 * (Thiết bị / Đang kết nối / Mất kết nối); hàng tìm kiếm; hàng nút Phát theo lịch,
 * Điều khiển, Phát ngay; khối âm lượng; bảng; dòng đếm ngược làm mới.
 *
 * Khuôn danh sách được viết MỘT LẦN ở đây (`DanhSachThietBiTheoMau`) và dùng lại
 * cho danh sách bảng điện tử MH-24 — chỉ khác cấu hình cột và nút.
 *
 * Số yêu cầu mạng khi mở màn hình: danh sách, nhà cung cấp, cây địa bàn và bốn
 * lời gọi đếm cho ô tóm tắt (`pageSize=1`, chỉ đọc `total`). Backend chưa có API
 * tóm tắt theo loại thiết bị nên phải đếm như vậy — xem báo cáo nợ kỹ thuật.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import {
  thietBiApi,
  type DongThietBi,
  type KetQuaHangLoat,
  type NutDiaBan,
  type TrangDuLieu,
} from '../api/thiet-bi-api';
import { dungHoan, dungTaiDuLieu, dungTuLamMoi } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { DaiChuaCoApi, NhanChuaCoApi, TruongLoc } from '../thanh-phan/MauGiaoDien';
import { tuoiThanhChu } from '../thanh-phan/NhanTrangThai';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { hienThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { BangKetQuaHangLoat } from '../thanh-phan/KetQuaHangLoat';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { MH08NganDieuKhien } from './MH08NganDieuKhien';
import { MenuDieuKhienThietBi } from './MenuDieuKhienThietBi';
import { HopPhatKhanCapCumLoa } from './HopPhatKhanCapCumLoa';
import { nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import './thiet-bi.css';

/** Khoảng chờ sau lần gõ cuối trước khi gửi yêu cầu tìm kiếm (chương 12). */
export const TRE_TIM_KIEM_MS = 400;

/** Chu kỳ tự làm mới, giây — trùng mặc định của `dungTuLamMoi`. */
const CHU_KY_LAM_MOI_GIAY = 60;

const KHOA_LOC = [
  'keyword',
  'trangThaiHienThi',
  'maNhaCungCap',
  'phuongThucKetNoi',
  'nguonId',
  'phienBanUngDung',
  'trangThai',
] as const;

export type LoaiThietBi = 'cum_thu_phat_thanh' | 'bang_tin_dien_tu';

type HamDanhSach = typeof thietBiApi.danhSach;

/* ============================================================================
 * Thành phần dùng chung cho các trang thiết bị (MH-01, MH-02, MH-24)
 * ========================================================================== */

/** Biểu tượng màn hình (bảng điện tử) — nét, cùng khuôn với BieuTuong. */
export function BieuTuongBangDienTu({ co = 40 }: { co?: number }): ReactNode {
  return (
    <svg
      width={co}
      height={co}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

export function BieuTuongThietBi({ loai, co = 40 }: { loai: LoaiThietBi; co?: number }): ReactNode {
  return loai === 'bang_tin_dien_tu' ? <BieuTuongBangDienTu co={co} /> : <BieuTuong ten="loa" co={co} />;
}

export interface TomTatThietBi {
  tong: number;
  /** Đang có trong tập trực tuyến của bộ đệm trạng thái. */
  dangKetNoi: number;
  dangPhat: number;
  /** Đang kết nối nhưng không phát (chờ phát, tạm dừng). */
  khongPhat: number;
  matKetNoi: number;
  chuaKichHoat: number;
}

/**
 * Đếm tóm tắt theo loại thiết bị và địa bàn bằng bốn lời gọi danh sách
 * `pageSize=1` (chỉ đọc `total`, lọc và đếm ở phía máy chủ). Đang kết nối được
 * suy ra = tổng − mất kết nối − chưa kích hoạt, đúng công thức của
 * `dashboard/summary`.
 */
export async function taiTomTatThietBi(
  danhSach: HamDanhSach,
  loaiThietBi: LoaiThietBi,
  nguonId: string | undefined,
  signal: AbortSignal,
): Promise<TomTatThietBi> {
  const dem = (trangThaiHienThi?: string): Promise<number> =>
    danhSach({ page: 1, pageSize: 1, loaiThietBi, nguonId, trangThaiHienThi }, signal).then((d) => d.total);
  const [tong, mat, chuaKichHoat, dangPhat] = await Promise.all([
    dem(),
    dem('mat_ket_noi'),
    dem('chua_kich_hoat'),
    dem('dang_phat'),
  ]);
  const dangKetNoi = Math.max(0, tong - mat - chuaKichHoat);
  return {
    tong,
    dangKetNoi,
    dangPhat: Math.min(dangPhat, dangKetNoi),
    khongPhat: Math.max(0, dangKetNoi - dangPhat),
    matKetNoi: mat,
    chuaKichHoat,
  };
}

/** Số hiển thị trong ô: đang tải lần đầu "…", lỗi "—" (lỗi chi tiết nằm ở bảng). */
export function soHienThi(so: number | undefined, coLoi: boolean): string {
  if (so === undefined) return coLoi ? viThietBi.chung.chuaCo : '…';
  return so.toLocaleString('vi-VN');
}

/** Ba ô tóm tắt DeviceSummary: Thiết bị / Đang kết nối (+ chú giải) / Mất kết nối. */
export function OTomTatThietBi({
  loai,
  duLieu,
  coLoi,
}: {
  loai: LoaiThietBi;
  duLieu: TomTatThietBi | null;
  coLoi: boolean;
}): ReactNode {
  const t = viThietBi.tomTat;
  return (
    <ul className="tb-tom-tat" aria-label={t.nhan} data-vung="tom-tat-thiet-bi" aria-busy={!duLieu && !coLoi}>
      <li className="tb-tom-tat__o" data-nhom="tong">
        <span className="tb-tom-tat__bieu-tuong" aria-hidden="true">
          <BieuTuongThietBi loai={loai} />
        </span>
        <span>
          <span className="tb-tom-tat__nhan">{t.thietBi}</span>
          <strong className="tb-tom-tat__so">{soHienThi(duLieu?.tong, coLoi)}</strong>
        </span>
      </li>
      <li className="tb-tom-tat__o" data-nhom="ket-noi">
        <span className="tb-tom-tat__bieu-tuong" aria-hidden="true">
          <BieuTuongThietBi loai={loai} />
        </span>
        <span>
          <span className="tb-tom-tat__nhan" title={t.ghiChuDangKetNoi}>
            {t.dangKetNoi}
          </span>
          <strong className="tb-tom-tat__so">{soHienThi(duLieu?.dangKetNoi, coLoi)}</strong>
        </span>
        <ChuGiaiPhat dangPhat={duLieu?.dangPhat} khongPhat={duLieu?.khongPhat} coLoi={coLoi} />
      </li>
      <li className="tb-tom-tat__o" data-nhom="mat-ket-noi">
        <span className="tb-tom-tat__bieu-tuong" aria-hidden="true">
          <BieuTuongThietBi loai={loai} />
        </span>
        <span>
          <span className="tb-tom-tat__nhan">{t.matKetNoi}</span>
          <strong className="tb-tom-tat__so">{soHienThi(duLieu?.matKetNoi, coLoi)}</strong>
        </span>
      </li>
    </ul>
  );
}

/** Thời điểm cập nhật hai dòng: giờ:phút:giây / ngày/tháng/năm (bản mẫu). */
/** Epoch giây → "HH:mm dd/MM/yyyy" theo giờ Việt Nam (không phụ thuộc múi giờ máy xem). */
function gioVn(epochGiay: number): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false,
    })
      .formatToParts(new Date(epochGiay * 1000))
      .map((x) => [x.type, x.value]),
  );
  return `${p.hour}:${p.minute} ${p.day}/${p.month}/${p.year}`;
}

export function OThoiDiem({
  epochGiay,
  tuoiGiay,
}: {
  epochGiay: number | null;
  tuoiGiay: number | null;
}): ReactNode {
  if (epochGiay === null) return '—';
  const d = new Date(epochGiay * 1000);
  const hai = (n: number): string => String(n).padStart(2, '0');
  // Tuổi dữ liệu không hiện trong ô (bản mẫu) nhưng vẫn xem được khi rê chuột.
  return (
    <time
      className="tb-thoi-diem"
      dateTime={d.toISOString()}
      title={`${vi.tuoiDuLieu.nhan}: ${tuoiThanhChu(tuoiGiay)}`}
    >
      {hai(d.getHours())}:{hai(d.getMinutes())}:{hai(d.getSeconds())}
      <span className="bang__dong-phu">
        {hai(d.getDate())}/{hai(d.getMonth() + 1)}/{d.getFullYear()}
      </span>
    </time>
  );
}

/**
 * "Mạng, Dung lượng dùng": TrangThaiKetNoi nguyên văn của DV008 ("4G, -70dbm",
 * "Ethernet"…) kèm dung lượng đã dùng đổi từ KB sang MB. Thiết bị không còn báo
 * (hết hạn bộ đệm trạng thái) hoặc báo rỗng → "Mất kết nối".
 */
export function mangDungLuong(d: Pick<DongThietBi, 'trangThaiKetNoi' | 'dungLuongSuDungKb'>): string {
  const mang = (d.trangThaiKetNoi ?? '').trim() || viThietBi.ketNoi.matKetNoi;
  if (d.dungLuongSuDungKb === null || d.dungLuongSuDungKb === undefined) return mang;
  return `${mang}, ${Math.round(d.dungLuongSuDungKb / 1024).toLocaleString('vi-VN')} MB`;
}

/* ---------------------- Cột "Kết nối" của danh sách thiết bị ---------------------- */

const MAU_XANH = 'var(--online)';
const MAU_DO = 'var(--tt-mat-ket-noi-chu)';

/**
 * Bốn chỉ báo của bản mẫu Cụm loa:
 *   1. cột sóng — xanh: đang kết nối, đỏ: mất kết nối / chưa kích hoạt;
 *   2. đám mây — xanh: gói trạng thái mới nhất đã được trung ương xác nhận, đỏ: chưa;
 *   3. loa — xanh: đang phát, đỏ (gạch chéo): không phát;
 *   4. chữ kiểu kết nối — api (gián tiếp), mqtt, ws.
 * Màu không bao giờ là kênh duy nhất: mỗi biểu tượng có `title` và chữ ẩn cho trình
 * đọc màn hình, và nhãn trạng thái chữ (năm trạng thái Bảng 3.3) vẫn nằm trong ô.
 */
export function OKetNoiThietBi({ d }: { d: DongThietBi }): ReactNode {
  const t = viThietBi.ketNoi;
  const coKetNoi = d.trangThaiHienThi !== 'mat_ket_noi' && d.trangThaiHienThi !== 'chua_kich_hoat';
  const dongBo = d.dongBoTrungUong === true;
  const dangPhat = d.trangThaiHienThi === 'dang_phat';
  const kenh = d.kenhKetNoi ?? (d.phuongThucKetNoi === 'gian_tiep' ? 'api' : 'mqtt');
  const moTaSong = coKetNoi ? t.dangKetNoi : t.matKetNoi;
  const moTaMay = dongBo ? t.daDongBo : t.chuaDongBo;
  const moTaLoa = dangPhat ? t.dangPhat : t.khongPhat;
  return (
    <span className="tb-ket-noi" data-vung="o-ket-noi">
      <span className="tb-ket-noi__hang">
        <svg
          width={18}
          height={16}
          viewBox="0 0 18 16"
          role="img"
          aria-label={moTaSong}
          style={{ color: coKetNoi ? MAU_XANH : MAU_DO }}
        >
          <title>{moTaSong}</title>
          {[3, 6, 9, 12, 15].map((h, i) => (
            <rect key={h} x={i * 3.6} y={16 - h} width={2.4} height={h} rx={0.6} fill="currentColor" />
          ))}
        </svg>
        <svg
          width={18}
          height={16}
          viewBox="0 0 24 20"
          role="img"
          aria-label={moTaMay}
          style={{ color: dongBo ? MAU_XANH : MAU_DO }}
        >
          <title>{moTaMay}</title>
          <path
            d="M7 17h10.5a4.5 4.5 0 0 0 .6-8.96A6 6 0 0 0 6.4 9.1 4 4 0 0 0 7 17z"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinejoin="round"
          />
          {dongBo ? (
            <path
              d="M9 12.5l2 2 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
            />
          ) : (
            <path
              d="M12 9.5v3.5M12 15v.1"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
            />
          )}
        </svg>
        <svg
          width={18}
          height={16}
          viewBox="0 0 24 20"
          role="img"
          aria-label={moTaLoa}
          style={{ color: dangPhat ? MAU_XANH : MAU_DO }}
        >
          <title>{moTaLoa}</title>
          <path
            d="M3 7.5v5h4l5 4v-13l-5 4H3z"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinejoin="round"
          />
          {dangPhat ? (
            <path
              d="M15.5 7a4 4 0 0 1 0 6M18 4.5a7.5 7.5 0 0 1 0 11"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
            />
          ) : (
            <path
              d="M15.5 7.5l5 5M20.5 7.5l-5 5"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
            />
          )}
        </svg>
      </span>
      <span className="tb-ket-noi__kenh" title={t.kenh[kenh]}>
        {kenh}
      </span>
      {/* Nhãn trạng thái bằng chữ (Bảng 3.3) cho trình đọc màn hình. */}
      <span className="chi-cho-doc-man-hinh">{vi.trangThaiThietBi[d.trangThaiHienThi]}</span>
    </span>
  );
}

/** Chú giải Đang phát / Không phát — vạch màu luôn đi kèm chữ. */
export function ChuGiaiPhat({
  dangPhat,
  khongPhat,
  coLoi,
  lienKetDangPhat,
}: {
  dangPhat: number | undefined;
  khongPhat: number | undefined;
  coLoi: boolean;
  lienKetDangPhat?: string;
}): ReactNode {
  const t = viThietBi.tomTat;
  const nhanDangPhat = `${t.dangPhat}: ${soHienThi(dangPhat, coLoi)}`;
  return (
    <span className="tb-chu-giai">
      <span>
        <i className="tb-chu-giai__vach tb-chu-giai__vach--phat" aria-hidden="true" />
        {lienKetDangPhat ? <Link to={lienKetDangPhat}>{nhanDangPhat}</Link> : nhanDangPhat}
      </span>
      <span>
        <i className="tb-chu-giai__vach" aria-hidden="true" />
        {t.khongPhat}: {soHienThi(khongPhat, coLoi)}
      </span>
    </span>
  );
}

/**
 * "Dữ liệu được cập nhật sau : N s" — đếm ngược tới lần tự làm mới kế tiếp. Đặt
 * lại mỗi khi một lần tải hoàn tất hoặc khi tự làm mới chạy lại sau tạm dừng.
 */
export function DemNguocLamMoi({
  soLanTai,
  dangTamDung,
}: {
  soLanTai: number;
  dangTamDung: boolean;
}): ReactNode {
  const [con, datCon] = useState(CHU_KY_LAM_MOI_GIAY);
  useEffect(() => {
    datCon(CHU_KY_LAM_MOI_GIAY);
  }, [soLanTai, dangTamDung]);
  useEffect(() => {
    if (dangTamDung) return undefined;
    const h = setInterval(() => datCon((x) => (x <= 1 ? CHU_KY_LAM_MOI_GIAY : x - 1)), 1000);
    return () => clearInterval(h);
  }, [dangTamDung]);
  return (
    <p className="tb-dem-nguoc" data-vung="dem-nguoc-lam-moi" aria-live="off">
      {dangTamDung ? viThietBi.demNguoc.tamDung : dienVao(viThietBi.demNguoc.sau, { so: con })}
    </p>
  );
}

/** Hộp chọn đơn vị hành chính: rỗng = toàn bộ phạm vi được phân công. */
export function ChonDonViHanhChinh({
  id,
  giaTri,
  cay,
  onDoi,
  anNhan = false,
}: {
  id: string;
  giaTri: string;
  cay: NutDiaBan[];
  onDoi: (v: string) => void;
  /** Hàng lọc gọn: không có nhãn trên, tên trường đọc qua aria-label. */
  anNhan?: boolean;
}): ReactNode {
  const luaChon = cay.filter((n) => n.cap === 'tinh' || n.cap === 'xa');
  // Ô chọn dạng cây có tìm kiếm (tỉnh → xã/phường), như bản mẫu.
  const hop = (
    <ChonDiaBanCay
      id={id}
      nhan={viThietBi.danhSach.donViHanhChinh}
      giaTri={giaTri}
      cay={luaChon}
      onDoi={onDoi}
      nhanTatCa={viThietBi.chung.toanPhamVi}
      className="tb-o-rong"
    />
  );
  if (anNhan) return hop;
  return (
    <TruongLoc nhan={viThietBi.danhSach.donViHanhChinh} htmlFor={id}>
      {hop}
    </TruongLoc>
  );
}

/**
 * Khối âm lượng của bản mẫu: thanh trượt + ô số + "Cập nhật". Gửi lệnh mã 0 (đặt
 * âm lượng 0–100) cho các thiết bị đang chọn qua `POST /api/v1/devices/commands`,
 * có hộp xác nhận nêu số thiết bị và bảng kết quả ba nhóm.
 */
export function KhoiAmLuong({
  ids,
  giaTriBanDau,
  guiLenh,
  choPhep,
  onXong,
}: {
  ids: string[];
  giaTriBanDau: number | null;
  guiLenh: typeof thietBiApi.guiLenh | undefined;
  choPhep: boolean;
  onXong: () => void;
}): ReactNode {
  const [giaTri, datGiaTri] = useState<number>(giaTriBanDau ?? 50);
  const [hoi, datHoi] = useState(false);
  const [dangGui, datDangGui] = useState(false);
  const [ketQua, datKetQua] = useState<KetQuaHangLoat | null>(null);
  const [loi, datLoi] = useState<string | null>(null);

  // Chọn dòng khác thì lấy lại giá trị chung của các dòng mới chọn.
  useEffect(() => {
    if (giaTriBanDau !== null) datGiaTri(giaTriBanDau);
  }, [giaTriBanDau]);

  const kep = (n: number): number => Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0)));
  const khoa = !guiLenh || !choPhep || ids.length === 0 || dangGui;

  const gui = async (dsId: string[]): Promise<void> => {
    if (!guiLenh) return;
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await guiLenh({ maLenh: '0', thamSo: String(giaTri), deviceIds: dsId });
      // Khách yêu cầu: gửi xong không hiện bảng kết quả; chỉ hiện khi có thiết bị lỗi để gửi lại.
      datKetQua(kq.loi.length > 0 ? kq : null);
      if (kq.loi.length === 0) hienThongBaoNhanh(viThietBi.danhSach.daCapNhatAmLuong);
      onXong();
    } catch (e) {
      datLoi(e instanceof Error ? e.message : String(e));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <>
      <div className="tb-am-luong" data-vung="khoi-am-luong" title={viThietBi.danhSach.amLuongGoiY}>
        <span className="tb-am-luong__bieu-tuong" aria-hidden="true">
          <BieuTuong ten="loa" co={16} />
        </span>
        <input
          type="range"
          min={0}
          max={100}
          value={giaTri}
          aria-label={viThietBi.danhSach.amLuong}
          onChange={(e) => datGiaTri(kep(Number(e.target.value)))}
          disabled={!guiLenh || !choPhep}
        />
        <input
          type="number"
          className="tb-am-luong__so"
          min={0}
          max={100}
          value={giaTri}
          aria-label={viThietBi.danhSach.giaTriAmLuong}
          onChange={(e) => datGiaTri(kep(Number(e.target.value)))}
          disabled={!guiLenh || !choPhep}
        />
        <span className="tb-hoi-neo">
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="cap-nhat-am-luong"
            disabled={khoa}
            aria-expanded={hoi}
            onClick={() => datHoi(!hoi)}
          >
            {dangGui ? viThietBi.danhSach.dangGui : viThietBi.danhSach.capNhat}
          </button>
          {hoi ? (
            <BongXacNhan
              tieuDe={viThietBi.danhSach.hoiAmLuongTieuDe}
              cauHoi={viThietBi.danhSach.hoiAmLuong}
              onHuy={() => datHoi(false)}
              onDongY={() => {
                datHoi(false);
                void gui(ids);
              }}
            />
          ) : null}
        </span>
      </div>
      {loi ? (
        <p className="tb-loi tb-am-luong__ket-qua" role="alert">
          {loi}
        </p>
      ) : null}
      {ketQua ? (
        <div className="tb-am-luong__ket-qua">
          <BangKetQuaHangLoat
            ketQua={ketQua}
            dangThuLai={dangGui}
            onThuLaiPhanLoi={(dsLoi) => void gui(dsLoi)}
            onDong={() => datKetQua(null)}
          />
        </div>
      ) : null}
    </>
  );
}

/** Bong bóng xác nhận nhỏ ngay trên nút (theo mẫu), đóng khi bấm ra ngoài hoặc Esc. */
function BongXacNhan({
  tieuDe,
  cauHoi,
  onHuy,
  onDongY,
}: {
  tieuDe: string;
  cauHoi: string;
  onHuy: () => void;
  onDongY: () => void;
}): ReactNode {
  const vung = useRef<HTMLDivElement | null>(null);
  const nutDongY = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    nutDongY.current?.focus();
    const ngoai = (e: MouseEvent): void => {
      const cha = vung.current?.parentElement;
      if (cha && !cha.contains(e.target as Node)) onHuy();
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onHuy();
    };
    document.addEventListener('mousedown', ngoai);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', ngoai);
      document.removeEventListener('keydown', esc);
    };
  }, [onHuy]);
  return (
    <div className="tb-hoi" role="alertdialog" aria-label={tieuDe} ref={vung} data-vung="hoi-cap-nhat">
      <p className="tb-hoi__tieu-de">
        <span className="tb-hoi__dau" aria-hidden="true">
          !
        </span>
        {tieuDe}
      </p>
      <p className="tb-hoi__cau">{cauHoi}</p>
      <div className="tb-hoi__nut">
        <button type="button" className="nut nut--nho" onClick={onHuy}>
          {viThietBi.danhSach.khongDongY}
        </button>
        <button
          type="button"
          className="nut nut--nho nut--chinh"
          data-hanh-dong="dong-y-cap-nhat"
          ref={nutDongY}
          onClick={onDongY}
        >
          {viThietBi.danhSach.dongY}
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
 * Khuôn danh sách thiết bị theo bản mẫu deviceView
 * ========================================================================== */

export interface ApiDanhSachThietBi {
  danhSach: HamDanhSach;
  nhaCungCap?: typeof thietBiApi.nhaCungCap;
  guiLenh?: typeof thietBiApi.guiLenh;
  cayDiaBan?: typeof thietBiApi.cayDiaBan;
}

interface CauHinhDanhSach {
  loai: LoaiThietBi;
  maManHinh: string;
  tieuDe: string;
  nhanDieuHuong: string;
  /** Đường dẫn chi tiết một thiết bị. */
  duongDanChiTiet: (d: DongThietBi) => string;
  duongDanPhatTheoLich: string;
  /** Có thì nút Phát ngay mở trang này; không có thì nút khoá + Chưa có API. */
  duongDanPhatNgay?: string;
  /**
   * `true`: nút Phát ngay mở hộp Phát khẩn cấp (DV003) ngay trên trang — có tích chọn
   * thì phát đúng thiết bị đó, không chọn thì phát toàn bộ địa bàn đang chọn.
   */
  phatKhanCapTaiCho?: boolean;
  /** Các cột riêng của loại thiết bị, chèn sau cột Mã nhà cung cấp. */
  cotRieng: CotBang<DongThietBi>[];
  /** Khóa các cột chung không hiển thị ở loại thiết bị này. */
  anCot?: readonly string[];
  /** Các cột chưa có API, liệt kê trong dải cảnh báo dưới bảng. */
  canApiCot: readonly string[];
  hanhDongDau: ReactNode;
  moTaRong?: string;
  nhanHanhDongRong?: string;
  onHanhDongRong?: () => void;
}

export function DanhSachThietBiTheoMau({
  api,
  cauHinh,
}: {
  api: ApiDanhSachThietBi;
  cauHinh: CauHinhDanhSach;
}): ReactNode {
  const goi = api;
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const [thamSo, datThamSo] = useSearchParams();
  const t = viThietBi.danhSach;

  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [chonToanBo, datChonToanBo] = useState(false);
  const [moNgan, datMoNgan] = useState(false);
  const [moPhatKc, datMoPhatKc] = useState(false);
  const [thongBaoPhat, datThongBaoPhat] = useState<string | null>(null);
  const [oTim, datOTim] = useState(thamSo.get('keyword') ?? '');
  const tuKhoaHoan = dungHoan(oTim, TRE_TIM_KIEM_MS);

  const trang = Number(thamSo.get('page') ?? 1) || 1;
  const soDong = Number(thamSo.get('pageSize') ?? SO_DONG_MAC_DINH) || SO_DONG_MAC_DINH;
  const sapXep = thamSo.get('sapXep') ?? 'tenThietBi';
  const huongSapXep = (thamSo.get('huongSapXep') === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';

  /** Địa bàn mặc định là phạm vi người dùng; tham số trên đường dẫn thu hẹp thêm. */
  const nguonId = thamSo.get('nguonId') ?? phien.nguonIdHienTai ?? '';

  const dangLoc = useMemo(
    () =>
      KHOA_LOC.some((k) => (k === 'nguonId' ? false : (thamSo.get(k) ?? '') !== '')) ||
      tuKhoaHoan.trim().length >= 2,
    [thamSo, tuKhoaHoan],
  );

  const datMotLoc = useCallback(
    (khoa: string, giaTri: string): void => {
      const moi = new URLSearchParams(thamSo);
      if (giaTri) moi.set(khoa, giaTri);
      else moi.delete(khoa);
      // Đổi bộ lọc thì về trang 1; giữ nguyên trang sẽ cho ra trang trống.
      moi.set('page', '1');
      datThamSo(moi, { replace: true });
      datDaChon(new Set());
      datChonToanBo(false);
    },
    [thamSo, datThamSo],
  );

  /** Xóa lọc đưa về MẶC ĐỊNH THEO PHẠM VI ĐỊA BÀN, không về rỗng (Bảng 3.1). */
  const xoaLoc = useCallback((): void => {
    const moi = new URLSearchParams();
    moi.set('page', '1');
    moi.set('pageSize', String(soDong));
    if (phien.nguonIdHienTai) moi.set('nguonId', phien.nguonIdHienTai);
    datThamSo(moi, { replace: true });
    datOTim('');
    datDaChon(new Set());
    datChonToanBo(false);
  }, [datThamSo, soDong, phien.nguonIdHienTai]);

  // Bộ lọc đang xem (không gồm phân trang) — dùng chung cho danh sách và Xuất Excel.
  const boLoc = {
    loaiThietBi: cauHinh.loai,
    keyword: tuKhoaHoan.trim().length >= 2 ? tuKhoaHoan.trim() : undefined,
    trangThaiHienThi: thamSo.get('trangThaiHienThi') ?? undefined,
    maNhaCungCap: thamSo.get('maNhaCungCap') ?? undefined,
    phuongThucKetNoi: thamSo.get('phuongThucKetNoi') ?? undefined,
    phienBanUngDung: thamSo.get('phienBanUngDung') ?? undefined,
    trangThai: thamSo.get('trangThai') ?? undefined,
    nguonId: nguonId || undefined,
    sapXep,
    huongSapXep,
  };
  const [dangXuat, datDangXuat] = useState(false);
  const xuatExcel = async (): Promise<void> => {
    datDangXuat(true);
    try {
      const so = await thietBiApi.xuatExcel(boLoc);
      hienThongBaoNhanh(dienVao(t.daXuatExcel, { so }));
    } catch (e) {
      hienThongBaoNhanh(dienVao(t.loiXuatExcel, { loi: e instanceof Error ? e.message : String(e) }), 'loi');
    } finally {
      datDangXuat(false);
    }
  };

  // Danh sách chính khai báo TRƯỚC các lời gọi đếm để là yêu cầu đầu tiên đi ra.
  const kq = dungTaiDuLieu<TrangDuLieu<DongThietBi>>({
    tai: (signal) =>
      goi.danhSach(
        {
          page: trang,
          pageSize: soDong,
          loaiThietBi: cauHinh.loai,
          // Từ khóa dưới hai ký tự không gửi lên (Bảng 3.1).
          keyword: tuKhoaHoan.trim().length >= 2 ? tuKhoaHoan.trim() : undefined,
          trangThaiHienThi: thamSo.get('trangThaiHienThi') ?? undefined,
          maNhaCungCap: thamSo.get('maNhaCungCap') ?? undefined,
          phuongThucKetNoi: thamSo.get('phuongThucKetNoi') ?? undefined,
          phienBanUngDung: thamSo.get('phienBanUngDung') ?? undefined,
          trangThai: thamSo.get('trangThai') ?? undefined,
          nguonId: nguonId || undefined,
          sapXep,
          huongSapXep,
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [
      trang,
      soDong,
      tuKhoaHoan,
      sapXep,
      huongSapXep,
      nguonId,
      thamSo.get('trangThaiHienThi'),
      thamSo.get('maNhaCungCap'),
      thamSo.get('phuongThucKetNoi'),
      thamSo.get('phienBanUngDung'),
      thamSo.get('trangThai'),
    ],
  });

  // Ô tóm tắt chỉ theo loại thiết bị và địa bàn, đúng sumDev của bản mẫu.
  const tomTat = dungTaiDuLieu<TomTatThietBi>({
    tai: (signal) => taiTomTatThietBi(goi.danhSach, cauHinh.loai, nguonId || undefined, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [cauHinh.loai, nguonId],
  });

  const ncc = dungTaiDuLieu<{ maNhaCungCap: string; ten: string }[]>({
    tai: (signal) => (goi.nhaCungCap ? goi.nhaCungCap(signal) : Promise.resolve([])),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => (goi.cayDiaBan ? goi.cayDiaBan({}, signal) : Promise.resolve([])),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  /** Tự làm mới 60 giây; DỪNG khi tab ẩn hoặc khi đang chọn dòng, đang mở ngăn. */
  const lamMoi = dungTuLamMoi(
    () => {
      kq.taiLai();
      tomTat.taiLai();
    },
    { dangChonDong: daChon.size > 0, dangMoNgan: moNgan },
  );

  const dong = kq.duLieu?.items ?? [];
  const viTri = useMemo(() => new Map(dong.map((d, i) => [d.id, i])), [dong]);
  const batDau = (trang - 1) * soDong;
  const coQuyenDieuKhien = phien.coQuyen('device.control') && goi.guiLenh !== undefined;

  const tatCaCot: CotBang<DongThietBi>[] = [
    {
      khoa: 'stt',
      nhan: t.cot.stt,
      ve: (d) => batDau + (viTri.get(d.id) ?? 0) + 1,
    },
    {
      khoa: 'trangThai',
      nhan: t.cot.ketNoi,
      sapXepDuoc: true,
      // Cụm biểu tượng theo bản mẫu: sóng, đồng bộ trung ương, loa + kiểu kết nối.
      ve: (d) => <OKetNoiThietBi d={d} />,
    },
    {
      khoa: 'nguonId',
      nhan: t.cot.diaBan,
      sapXepDuoc: true,
      // Có thôn thì thôn trên, xã dưới; không có thôn thì chỉ tên xã.
      ve: (d) =>
        d.tenThon || d.maThon ? (
          <>
            {d.tenThon ?? d.maThon}
            <span className="bang__dong-phu">{d.tenXa ?? d.nguonId}</span>
          </>
        ) : (
          (d.tenXa ?? d.nguonId)
        ),
    },
    {
      khoa: 'tenThietBi',
      nhan: t.cot.tenThietBi,
      sapXepDuoc: true,
      ve: (d) => (
        <>
          <Link to={cauHinh.duongDanChiTiet(d)}>{d.tenThietBi ?? d.cumLoaId}</Link>
          <span className="tb-ma-chip">{d.cumLoaId}</span>
        </>
      ),
    },
    {
      khoa: 'amLuong',
      nhan: t.cot.amLuong,
      canGiua: true,
      ve: (d) => (d.amLuong === null ? '—' : String(d.amLuong)),
    },
    {
      khoa: 'noiDungPhat',
      nhan: t.cot.noiDungPhat,
      ve: (d) => d.noiDungPhat ?? '',
    },
    {
      khoa: 'lichPhatMoiNhat',
      nhan: t.cot.lichPhatMoiNhat,
      ve: (d) =>
        d.lichPhatMoiNhat ? (
          <span className="tb-lich-moi" title={`${t.maLich}: ${d.lichPhatMoiNhat.lichPhatId}`}>
            <strong>{d.lichPhatMoiNhat.tenLichPhat ?? d.lichPhatMoiNhat.lichPhatId}</strong>
            <span className="bang__dong-phu">{gioVn(d.lichPhatMoiNhat.thoiDiem)}</span>
          </span>
        ) : (
          <span className="tb-chu-mo">{viThietBi.chung.chuaCo}</span>
        ),
    },
    {
      khoa: 'capNhatLuc',
      nhan: t.cot.capNhatLuc,
      sapXepDuoc: true,
      ve: (d) => <OThoiDiem epochGiay={d.capNhatLuc} tuoiGiay={d.tuoiDuLieuGiay} />,
    },
    {
      khoa: 'tenNhaCungCap',
      nhan: t.cot.maNhaCungCap,
      sapXepDuoc: true,
      canGiua: true,
      // Chỉ mã (bản mẫu); tên nhà cung cấp ở chú thích khi rê chuột. Kiểu kết nối đã
      // hiện ở cột "Kết nối" (api / mqtt / ws).
      ve: (d) => <span title={d.tenNhaCungCap}>{d.maNhaCungCap}</span>,
    },
    ...cauHinh.cotRieng,
    {
      khoa: 'hanhDong',
      nhan: t.cot.thaoTac,
      ve: (d) => (
        <span className="bang__hanh-dong">
          <Link className="nut nut--nho" to={cauHinh.duongDanChiTiet(d)}>
            {vi.thietBi.moChiTiet}
          </Link>
          {coQuyenDieuKhien ? (
            <button
              type="button"
              className="nut nut--nho"
              onClick={() => {
                datDaChon(new Set([d.id]));
                datMoNgan(true);
              }}
            >
              {vi.thietBi.dieuKhienNhanh}
            </button>
          ) : null}
        </span>
      ),
    },
  ];
  const cot = cauHinh.anCot ? tatCaCot.filter((c) => !cauHinh.anCot?.includes(c.khoa)) : tatCaCot;

  const thietBiDaChon = dong.filter((d) => daChon.has(d.id));
  const amLuongChung = useMemo(() => {
    const ds = thietBiDaChon.map((d) => d.amLuong).filter((x): x is number => x !== null);
    return ds.length > 0 && ds.every((x) => x === ds[0]) ? (ds[0] as number) : null;
  }, [thietBiDaChon]);

  const doiTimKiem = (v: string): void => {
    datOTim(v);
    const moi = new URLSearchParams(thamSo);
    if (v) moi.set('keyword', v);
    else moi.delete('keyword');
    moi.set('page', '1');
    datThamSo(moi, { replace: true });
  };

  return (
    <section className="man-hinh tb-danh-sach" data-man-hinh={cauHinh.maManHinh}>
      {/* Tiêu đề trang đã hiện ở thanh trên (như mẫu) — vệt đường dẫn và h1 chỉ
          còn cho trình đọc màn hình. */}
      <nav className="chi-cho-doc-man-hinh" aria-label={cauHinh.nhanDieuHuong}>
        <span>{cauHinh.nhanDieuHuong}</span> / <span>{cauHinh.tieuDe}</span>
      </nav>
      <h1 className="chi-cho-doc-man-hinh">{cauHinh.tieuDe}</h1>

      {/* Hàng trên: lọc đơn vị hành chính, trạng thái (+ các lọc khoanh vùng sự
          cố) bên trái — các nút trang và Xuất Excel góc phải. */}
      <div className="tb-hang tb-hang--giua-hai-dau">
        <div className="tb-loc-gon" data-vung="thanh-loc">
          <ChonDonViHanhChinh
            id="loc-don-vi"
            anNhan
            giaTri={thamSo.get('nguonId') ?? ''}
            cay={cay.duLieu ?? []}
            onDoi={(v) => datMotLoc('nguonId', v)}
          />
          <select
            id="loc-trang-thai"
            aria-label={t.trangThai}
            value={thamSo.get('trangThaiHienThi') ?? ''}
            onChange={(e) => datMotLoc('trangThaiHienThi', e.target.value)}
          >
            <option value="">{t.chonTrangThai}</option>
            {Object.entries(vi.trangThaiThietBi).map(([ma, nhan]) => (
              <option key={ma} value={ma}>
                {nhan}
              </option>
            ))}
          </select>
          {dangLoc ? (
            <button type="button" className="nut" onClick={xoaLoc} data-hanh-dong="xoa-loc">
              {vi.bang.xoaLoc}
            </button>
          ) : null}
        </div>
        <div className="tb-nut-phai">
          {cauHinh.hanhDongDau}
          <button
            type="button"
            className="nut tb-nut-excel"
            data-hanh-dong="xuat-excel"
            disabled={dangXuat}
            onClick={() => void xuatExcel()}
          >
            {dangXuat ? t.dangXuatExcel : t.xuatExcel}
          </button>
        </div>
      </div>

      <OTomTatThietBi loai={cauHinh.loai} duLieu={tomTat.duLieu} coLoi={tomTat.trangThai === 'loi'} />

      {/* Hàng thao tác một dòng như mẫu: tìm kiếm | âm lượng | Phát theo lịch,
          Điều khiển, Phát ngay. */}
      <div className="tb-hang-thao-tac" data-vung="hang-thao-tac">
        <div className="tb-nhom-tim">
          <input
            id="loc-tu-khoa"
            type="search"
            aria-label={t.maThietBi}
            placeholder={t.maThietBiGoiY}
            value={oTim}
            onChange={(e) => doiTimKiem(e.target.value)}
          />
          {/* Nhà cung cấp là bộ lọc BẮT BUỘC (khoanh vùng sự cố). */}
          <select
            id="loc-ncc"
            aria-label={t.nhaCungCap}
            value={thamSo.get('maNhaCungCap') ?? ''}
            onChange={(e) => datMotLoc('maNhaCungCap', e.target.value)}
          >
            <option value="">{t.chonNhaCungCap}</option>
            {(ncc.duLieu ?? []).map((n) => (
              <option key={n.maNhaCungCap} value={n.maNhaCungCap}>
                {n.ten}
              </option>
            ))}
          </select>
          <button type="button" className="nut nut--chinh" data-hanh-dong="tim-kiem" onClick={kq.taiLai}>
            <BieuTuong ten="tim-kiem" co={16} />
            {t.timKiem}
          </button>
        </div>
        <KhoiAmLuong
          ids={thietBiDaChon.map((d) => d.id)}
          giaTriBanDau={amLuongChung}
          guiLenh={goi.guiLenh}
          choPhep={coQuyenDieuKhien}
          onXong={kq.taiLai}
        />
        <div className="tb-nhom-nut">
          <Link className="nut nut--chinh" to={cauHinh.duongDanPhatTheoLich}>
            <BieuTuong ten="lich" co={16} />
            {t.phatTheoLich}
          </Link>
          <MenuDieuKhienThietBi
            thietBi={thietBiDaChon}
            choPhep={coQuyenDieuKhien}
            guiLenh={goi.guiLenh}
            onXong={kq.taiLai}
          />
          {cauHinh.phatKhanCapTaiCho ? (
            <button
              type="button"
              className="nut nut--nguy-hiem"
              data-hanh-dong="phat-ngay"
              onClick={() => {
                datThongBaoPhat(null);
                datMoPhatKc(true);
              }}
            >
              <BieuTuong ten="phat" co={14} />
              {t.phatNgay}
            </button>
          ) : cauHinh.duongDanPhatNgay ? (
            <button
              type="button"
              className="nut nut--nguy-hiem"
              data-hanh-dong="phat-ngay"
              disabled={thietBiDaChon.length === 0}
              title={thietBiDaChon.length === 0 ? t.phatNgayGoiY : undefined}
              onClick={() => dieuHuong(cauHinh.duongDanPhatNgay as string)}
            >
              <BieuTuong ten="phat" co={14} />
              {t.phatNgay}
            </button>
          ) : (
            <span className="tb-nut-kem-nhan">
              <button type="button" className="nut nut--nguy-hiem" data-hanh-dong="phat-ngay" disabled>
                <BieuTuong ten="phat" co={14} />
                {t.phatNgay}
              </button>
              <NhanChuaCoApi />
            </span>
          )}
        </div>
      </div>

      <BangDuLieu<DongThietBi>
        nhan={cauHinh.tieuDe}
        cot={cot}
        dong={dong}
        khoaDong={(d) => d.id}
        nhanDong={(d) => `${d.tenThietBi ?? d.cumLoaId} (${d.cumLoaId})`}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={(so) => {
          const moi = new URLSearchParams(thamSo);
          moi.set('page', String(so));
          datThamSo(moi, { replace: true });
        }}
        onDoiSoDong={(s) => {
          const moi = new URLSearchParams(thamSo);
          moi.set('pageSize', String(s));
          moi.set('page', '1');
          datThamSo(moi, { replace: true });
        }}
        sapXep={sapXep}
        huongSapXep={huongSapXep}
        onDoiSapXep={(k, h) => {
          const moi = new URLSearchParams(thamSo);
          moi.set('sapXep', k);
          moi.set('huongSapXep', h);
          datThamSo(moi, { replace: true });
        }}
        daChon={daChon}
        onDoiChon={(ds) => {
          datDaChon(ds);
          if (ds.size === 0) datChonToanBo(false);
        }}
        chonToanBoKetQua={chonToanBo}
        onChonToanBoKetQua={datChonToanBo}
        // Khách yêu cầu bỏ thanh "Đã chọn N dòng": điều khiển dùng nút trên thanh công cụ.
        anThanhHangLoat
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={cauHinh.moTaRong}
        nhanHanhDongRong={cauHinh.nhanHanhDongRong}
        onHanhDongRong={cauHinh.onHanhDongRong}
      />

      <DemNguocLamMoi soLanTai={kq.soLanTai} dangTamDung={lamMoi.dangTamDung} />

      {cauHinh.canApiCot.length > 0 ? <DaiChuaCoApi canApi={cauHinh.canApiCot} /> : null}

      {thongBaoPhat ? (
        <p className="tb-thong-bao-phat" role="status">
          {thongBaoPhat}
        </p>
      ) : null}

      {moPhatKc ? (
        <HopPhatKhanCapCumLoa
          thietBi={thietBiDaChon}
          dich={dichPhatTheoPhien(phien)}
          onDong={() => datMoPhatKc(false)}
          onDaGui={(tb) => {
            datMoPhatKc(false);
            datThongBaoPhat(tb);
            kq.taiLai();
          }}
        />
      ) : null}

      {moNgan && thietBiDaChon.length > 0 ? (
        <MH08NganDieuKhien
          thietBi={thietBiDaChon}
          guiLenh={goi.guiLenh}
          onDong={() => {
            datMoNgan(false);
            kq.taiLai();
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Đích phát khi không tích chọn thiết bị: địa bàn đang chọn ở menu người dùng, không
 * chọn thì phạm vi được phân công, không có nữa thì cả tỉnh.
 */
function dichPhatTheoPhien(phien: ReturnType<typeof dungPhien>): { diaBan: string[]; tenDiaBan: string } {
  // Tài khoản cấp xã: đích luôn là xã được phân công, hiện bằng tên xã.
  if (phien.khoaDiaBan) return { diaBan: [phien.khoaDiaBan.ma], tenDiaBan: phien.khoaDiaBan.ten };
  const chon = phien.phamViDangChon.length > 0 ? phien.phamViDangChon : phien.phamViDuocPhanCong;
  const maTinh = phien.hoSo?.maTinh;
  if (chon.length > 0) {
    const laCaTinh = chon.length === 1 && chon[0] === maTinh;
    return {
      diaBan: chon,
      tenDiaBan: laCaTinh ? (phien.hoSo?.tenTinh ?? (chon[0] as string)) : nhanPhamVi(chon),
    };
  }
  return maTinh
    ? { diaBan: [maTinh], tenDiaBan: phien.hoSo?.tenTinh ?? maTinh }
    : { diaBan: [], tenDiaBan: '—' };
}

/* ============================================================================
 * MH-02 — trang "Cụm loa"
 * ========================================================================== */

export interface MH02Props {
  /** Ghi đè lớp API, dùng cho kiểm thử thành phần. */
  api?: Pick<typeof thietBiApi, 'danhSach' | 'nhaCungCap' | 'guiLenh'> &
    Partial<Pick<typeof thietBiApi, 'cayDiaBan'>>;
}

export function MH02DanhSachThietBi({ api }: MH02Props = {}): ReactNode {
  const goi: ApiDanhSachThietBi = api ?? thietBiApi;
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const t = viThietBi.danhSach;
  const coQuyenTao = phien.coQuyen('device.create');

  return (
    <DanhSachThietBiTheoMau
      api={goi}
      cauHinh={{
        loai: 'cum_thu_phat_thanh',
        maManHinh: 'MH-02',
        tieuDe: t.tieuDeCumLoa,
        nhanDieuHuong: vi.dieuHuong.thietBi,
        duongDanChiTiet: (d) => `/thiet-bi/${d.id}`,
        duongDanPhatTheoLich: '/lich-phat/moi',
        duongDanPhatNgay: '/phat-song/ngay',
        phatKhanCapTaiCho: true,
        // Theo mẫu: không có cột Nội dung đang phát, Dòng điện, Thao tác (tên thiết
        // bị là liên kết chi tiết; điều khiển qua ô chọn + nút Điều khiển).
        anCot: ['noiDungPhat', 'hanhDong'],
        cotRieng: [
          {
            khoa: 'nhietDo',
            nhan: t.cot.nhietDo,
            canGiua: true,
            ve: (d) => (d.nhietDo === null ? '—' : `${d.nhietDo}${t.doC}`),
          },
          {
            khoa: 'mangDungLuong',
            nhan: t.cot.mangDungLuong,
            ve: (d) => mangDungLuong(d),
          },
          {
            khoa: 'phienBanUngDung',
            nhan: t.cot.phienBan,
            ve: (d) => d.phienBanUngDung ?? '—',
          },
        ],
        canApiCot: [],
        // Theo mẫu: trang không có hàng nút đầu trang. Bản đồ nằm trên menu,
        // Khai báo / Nhập khẩu nằm ở trang Quản trị cụm loa và ở trạng thái rỗng.
        hanhDongDau: null,
        nhanHanhDongRong: coQuyenTao ? vi.thietBi.khaiBaoMoi : undefined,
        onHanhDongRong: coQuyenTao ? () => dieuHuong('/thiet-bi/moi') : undefined,
      }}
    />
  );
}
